<form [formGroup]="form">
  <label>{{ 'dashboard.edit.title' | translate }}</label>
  <input type="text" formControlName="title" class="form-control" />

  <label>{{ 'dashboard.edit.description' | translate }}</label>
  <input type="text" formControlName="description" class="form-control" />

  @if (!isTemplateDashboard) {
    <label>{{
      'dashboard.edit.JobResultAnalyticsDashboardComponent.dataConnections'
        | translate
    }}</label>
    <p-multiselect
      [options]="dataConnections"
      formControlName="dataConnectionIds"
      optionValue="id"
      [filter]="true"
      [showClear]="true"
      [showToggleAll]="false"
      optionLabel="name"
      class="w-100"
    />
  }

  <label>{{
    'dashboard.edit.JobResultAnalyticsDashboardComponent.statuses' | translate
  }}</label>
  <p-multiselect
    [options]="statusOptions"
    formControlName="statuses"
    optionValue="value"
    [filter]="false"
    [showClear]="true"
    [showToggleAll]="false"
    optionLabel="key"
    class="w-100"
  >
    <ng-template let-selectedOption pTemplate="selectedItem">
      {{ 'job.statusOptions.' + selectedOption.key | translate }}
    </ng-template>
    <ng-template let-option pTemplate="item">
      {{ 'job.statusOptions.' + option.key | translate }}
    </ng-template>
  </p-multiselect>

  <label>{{
    'dashboard.edit.JobResultAnalyticsDashboardComponent.range' | translate
  }}</label>
  <p-select
    formControlName="range"
    [options]="rangeOptions"
    optionLabel="key"
    optionValue="value"
    class="w-100"
  >
    <ng-template let-selectedOption pTemplate="selectedItem">
      {{
        'dashboard.edit.JobDashboardComponent.rangeOptions.' +
          selectedOption.key | translate
      }}
    </ng-template>
    <ng-template let-option pTemplate="item">
      {{
        'dashboard.edit.JobDashboardComponent.rangeOptions.' + option.key
          | translate
      }}
    </ng-template>
  </p-select>

  @if (form.get('range')!.value === customRange) {
    <label>{{
      'dashboard.edit.JobDashboardComponent.rangeOptions.CUSTOM' | translate
    }}</label>
    <p-datepicker
      required
      [(ngModel)]="dateRange"
      [ngModelOptions]="{ standalone: true }"
      (ngModelChange)="customRangeChange($event)"
      selectionMode="range"
      dateFormat="dd.mm.yy"
      [showIcon]="true"
      [showButtonBar]="true"
      [numberOfMonths]="1"
      styleClass="w-100"
      inputStyleClass="w-100"
      [class.is-invalid]="dateRangeError"
    />
    @if (dateRangeError) {
      <div class="invalid-feedback d-block">{{ dateRangeError }}</div>
    }
    <small class="text-muted d-block mb-2">
      {{
        'dashboard.edit.JobResultAnalyticsDashboardComponent.maxDateRange'
          | translate: { days: maxDateRangeDays }
      }}
    </small>
  }

  <label>{{
    'dashboard.edit.JobResultAnalyticsDashboardComponent.metricType' | translate
  }}</label>
  <p-select
    formControlName="metricType"
    [options]="metricTypeOptions"
    optionLabel="key"
    optionValue="value"
    class="w-100"
    (onChange)="onMetricTypeChange()"
  >
    <ng-template let-selectedOption pTemplate="selectedItem">
      {{
        'dashboard.edit.JobResultAnalyticsDashboardComponent.metricTypes.' +
          selectedOption.key | translate
      }}
    </ng-template>
    <ng-template let-option pTemplate="item">
      {{
        'dashboard.edit.JobResultAnalyticsDashboardComponent.metricTypes.' +
          option.key | translate
      }}
    </ng-template>
  </p-select>

  @if (form.get('metricType')!.value === AnalyticsMetricType.AGGREGATE) {
    <label>{{
      'dashboard.edit.JobResultAnalyticsDashboardComponent.chartType'
        | translate
    }}</label>
    <p-select
      formControlName="chartType"
      [options]="chartTypes"
      optionLabel="key"
      optionValue="value"
      class="w-100"
    >
      <ng-template let-selectedOption pTemplate="selectedItem">
        {{
          'dashboard.edit.JobResultAnalyticsDashboardComponent.chartTypes.' +
            selectedOption.key | translate
        }}
      </ng-template>
      <ng-template let-option pTemplate="item">
        {{
          'dashboard.edit.JobResultAnalyticsDashboardComponent.chartTypes.' +
            option.key | translate
        }}
      </ng-template>
    </p-select>
  }

  <hr class="my-3" />

  @if (form.get('metricType')!.value === AnalyticsMetricType.AGGREGATE) {
    <div class="d-flex justify-content-between align-items-center mb-2">
      <label class="mb-0">
        {{
          'dashboard.edit.JobResultAnalyticsDashboardComponent.aggregations'
            | translate
        }}
        <span class="text-muted small ms-1"
          >({{ aggregations.length }}/{{ maxAggregations }})</span
        >
      </label>
      <div>
        @if (!isTemplateDashboard) {
          <button
            type="button"
            class="btn btn-sm btn-outline-secondary me-2"
            (click)="discoverFields()"
            [disabled]="isLoadingFields"
          >
            @if (isLoadingFields) {
              <span class="spinner-border spinner-border-sm me-1"></span>
            }
            {{
              'dashboard.edit.JobResultAnalyticsDashboardComponent.discoverFields'
                | translate
            }}
          </button>
        }
        <button
          type="button"
          class="btn btn-sm btn-outline-primary"
          (click)="addAggregation()"
          [disabled]="aggregations.length >= maxAggregations"
        >
          {{
            'dashboard.edit.JobResultAnalyticsDashboardComponent.addAggregation'
              | translate
          }}
        </button>
      </div>
    </div>
    @if (aggregations.length >= maxAggregations) {
      <div class="alert alert-info py-1 mb-2">
        {{
          'dashboard.edit.JobResultAnalyticsDashboardComponent.maxAggregationsReached'
            | translate: { max: maxAggregations }
        }}
      </div>
    }

    @if (availableFields.length === 0 && !isLoadingFields) {
      <div class="alert alert-info py-2">
        {{
          'dashboard.edit.JobResultAnalyticsDashboardComponent.noFieldsFound'
            | translate
        }}
      </div>
    }

    <div formArrayName="aggregations">
      @for (agg of aggregations.controls; track agg; let i = $index) {
        <div class="card mb-2">
          <div class="card-body py-2">
            <div [formGroupName]="i" class="row align-items-end">
              <div class="col-5">
                <label class="small">{{
                  'dashboard.edit.JobResultAnalyticsDashboardComponent.fieldKey'
                    | translate
                }}</label>
                <p-select
                  formControlName="fieldKey"
                  [options]="getFieldOptions()"
                  optionLabel="label"
                  optionValue="value"
                  [filter]="true"
                  [showClear]="true"
                  placeholder="Select field"
                  class="w-100"
                  (onChange)="onAggregationFieldChange(i)"
                />
              </div>
              <div class="col-5">
                <label class="small">{{
                  'dashboard.edit.JobResultAnalyticsDashboardComponent.aggregationType'
                    | translate
                }}</label>
                <p-select
                  formControlName="type"
                  [options]="getAggregationTypeOptionsForRow(i)"
                  optionLabel="key"
                  optionValue="value"
                  class="w-100"
                  (onChange)="onAggregationTypeChange(i)"
                >
                  <ng-template let-selectedOption pTemplate="selectedItem">
                    {{
                      'dashboard.edit.JobResultAnalyticsDashboardComponent.aggregationTypes.' +
                        selectedOption.key | translate
                    }}
                  </ng-template>
                  <ng-template let-option pTemplate="item">
                    {{
                      'dashboard.edit.JobResultAnalyticsDashboardComponent.aggregationTypes.' +
                        option.key | translate
                    }}
                  </ng-template>
                </p-select>
              </div>
              <div class="col-2">
                <button
                  type="button"
                  class="btn btn-sm btn-outline-danger w-100"
                  (click)="removeAggregation(i)"
                >
                  <span class="material-symbols-outlined">delete</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      }
    </div>
  }

  @if (form.get('metricType')!.value === AnalyticsMetricType.BUCKETED_AVERAGE) {
    <div class="d-flex justify-content-between align-items-center mb-2">
      <label class="mb-0">{{
        'dashboard.edit.JobResultAnalyticsDashboardComponent.bucketedAverage'
          | translate
      }}</label>
      @if (!isTemplateDashboard) {
        <button
          type="button"
          class="btn btn-sm btn-outline-secondary"
          (click)="discoverFields()"
          [disabled]="isLoadingFields"
        >
          @if (isLoadingFields) {
            <span class="spinner-border spinner-border-sm me-1"></span>
          }
          {{
            'dashboard.edit.JobResultAnalyticsDashboardComponent.discoverFields'
              | translate
          }}
        </button>
      }
    </div>

    @if (availableFields.length === 0 && !isLoadingFields) {
      <div class="alert alert-info py-2">
        {{
          'dashboard.edit.JobResultAnalyticsDashboardComponent.noFieldsFound'
            | translate
        }}
      </div>
    }

    <div class="row g-2">
      <div class="col-md-6">
        <label>
          {{
            'dashboard.edit.JobResultAnalyticsDashboardComponent.valueField'
              | translate
          }}
          <span class="text-danger">*</span>
        </label>
        <p-select
          formControlName="bucketValueFieldKey"
          [options]="getFieldOptions()"
          optionLabel="label"
          optionValue="value"
          [filter]="true"
          [showClear]="true"
          class="w-100"
          [class.ng-invalid]="
            isBucketedAverageControlInvalid('bucketValueFieldKey')
          "
          [class.ng-dirty]="
            isBucketedAverageControlInvalid('bucketValueFieldKey')
          "
        />
        @if (isBucketedAverageControlInvalid('bucketValueFieldKey')) {
          <div class="invalid-feedback d-block">
            {{
              'dashboard.edit.JobResultAnalyticsDashboardComponent.valueFieldRequired'
                | translate
            }}
          </div>
        }
        <small class="text-muted">
          {{
            'dashboard.edit.JobResultAnalyticsDashboardComponent.countDistinctValue'
              | translate
          }}
        </small>
      </div>

      <div class="col-md-6">
        <label>{{
          'dashboard.edit.JobResultAnalyticsDashboardComponent.bucketGranularity'
            | translate
        }}</label>
        <p-select
          formControlName="bucketGranularity"
          [options]="dateGranularityOptions"
          optionLabel="key"
          optionValue="value"
          class="w-100"
        >
          <ng-template let-selectedOption pTemplate="selectedItem">
            {{
              'dashboard.edit.JobResultAnalyticsDashboardComponent.dateGranularityOptions.' +
                selectedOption.key | translate
            }}
          </ng-template>
          <ng-template let-option pTemplate="item">
            {{
              'dashboard.edit.JobResultAnalyticsDashboardComponent.dateGranularityOptions.' +
                option.key | translate
            }}
          </ng-template>
        </p-select>
      </div>

      <div class="col-md-6">
        <label>{{
          'dashboard.edit.JobResultAnalyticsDashboardComponent.dateSource'
            | translate
        }}</label>
        <p-select
          formControlName="dateSourceType"
          [options]="dateSourceTypeOptions"
          optionLabel="key"
          optionValue="value"
          class="w-100"
        >
          <ng-template let-selectedOption pTemplate="selectedItem">
            {{
              'dashboard.edit.JobResultAnalyticsDashboardComponent.dateSourceTypes.' +
                selectedOption.key | translate
            }}
          </ng-template>
          <ng-template let-option pTemplate="item">
            {{
              'dashboard.edit.JobResultAnalyticsDashboardComponent.dateSourceTypes.' +
                option.key | translate
            }}
          </ng-template>
        </p-select>
      </div>

      @if (form.get('dateSourceType')!.value === DateSourceType.RESULT_FIELD) {
        <div class="col-md-6">
          <label>
            {{
              'dashboard.edit.JobResultAnalyticsDashboardComponent.dateSourceField'
                | translate
            }}
            <span class="text-danger">*</span>
          </label>
          <p-select
            formControlName="dateSourceFieldKey"
            [options]="getFieldOptions()"
            optionLabel="label"
            optionValue="value"
            [filter]="true"
            [showClear]="true"
            class="w-100"
            [class.ng-invalid]="
              isBucketedAverageControlInvalid('dateSourceFieldKey')
            "
            [class.ng-dirty]="
              isBucketedAverageControlInvalid('dateSourceFieldKey')
            "
          />
          @if (isBucketedAverageControlInvalid('dateSourceFieldKey')) {
            <div class="invalid-feedback d-block">
              {{
                'dashboard.edit.JobResultAnalyticsDashboardComponent.dateSourceFieldRequired'
                  | translate
              }}
            </div>
          }
        </div>
      }

      <div class="col-md-6">
        <label>{{
          'dashboard.edit.JobResultAnalyticsDashboardComponent.emptyBucketMode'
            | translate
        }}</label>
        <p-select
          formControlName="emptyBucketMode"
          [options]="emptyBucketModeOptions"
          optionLabel="key"
          optionValue="value"
          class="w-100"
        >
          <ng-template let-selectedOption pTemplate="selectedItem">
            {{
              'dashboard.edit.JobResultAnalyticsDashboardComponent.emptyBucketModes.' +
                selectedOption.key | translate
            }}
          </ng-template>
          <ng-template let-option pTemplate="item">
            {{
              'dashboard.edit.JobResultAnalyticsDashboardComponent.emptyBucketModes.' +
                option.key | translate
            }}
          </ng-template>
        </p-select>
        <small class="text-muted">
          {{
            'dashboard.edit.JobResultAnalyticsDashboardComponent.emptyBucketModeHint'
              | translate
          }}
        </small>
      </div>
    </div>
  }

  <hr class="my-3" />

  <div class="d-flex justify-content-between align-items-center mb-2">
    <label class="mb-0">
      {{
        'dashboard.edit.JobResultAnalyticsDashboardComponent.filters'
          | translate
      }}
      <span class="text-muted small ms-1"
        >({{ filters.length }}/{{ maxFilters }})</span
      >
    </label>
    <button
      type="button"
      class="btn btn-sm btn-outline-primary"
      (click)="addFilter()"
      [disabled]="filters.length >= maxFilters"
    >
      {{
        'dashboard.edit.JobResultAnalyticsDashboardComponent.addFilter'
          | translate
      }}
    </button>
  </div>
  @if (filters.length >= maxFilters) {
    <div class="alert alert-info py-1 mb-2">
      {{
        'dashboard.edit.JobResultAnalyticsDashboardComponent.maxFiltersReached'
          | translate: { max: maxFilters }
      }}
    </div>
  }

  <div formArrayName="filters">
    @for (filter of filters.controls; track filter; let i = $index) {
      <div class="card mb-2">
        <div class="card-body py-2">
          <div [formGroupName]="i" class="row align-items-end">
            <div class="col-4">
              <label class="small">{{
                'dashboard.edit.JobResultAnalyticsDashboardComponent.fieldKey'
                  | translate
              }}</label>
              <p-select
                formControlName="fieldKey"
                [options]="getFieldOptions()"
                optionLabel="label"
                optionValue="value"
                [filter]="true"
                [showClear]="true"
                placeholder="Select field"
                class="w-100"
              />
            </div>
            <div class="col-3">
              <label class="small">{{
                'dashboard.edit.JobResultAnalyticsDashboardComponent.operator'
                  | translate
              }}</label>
              <p-select
                formControlName="operator"
                [options]="filterOperators"
                optionLabel="key"
                optionValue="value"
                class="w-100"
              >
                <ng-template let-selectedOption pTemplate="selectedItem">
                  {{
                    'dashboard.edit.JobResultAnalyticsDashboardComponent.filterOperators.' +
                      selectedOption.key | translate
                  }}
                </ng-template>
                <ng-template let-option pTemplate="item">
                  {{
                    'dashboard.edit.JobResultAnalyticsDashboardComponent.filterOperators.' +
                      option.key | translate
                  }}
                </ng-template>
              </p-select>
            </div>
            <div class="col-3">
              <label class="small">{{
                'dashboard.edit.JobResultAnalyticsDashboardComponent.value'
                  | translate
              }}</label>
              <input
                type="text"
                formControlName="value"
                class="form-control form-control-sm"
              />
            </div>
            <div class="col-2">
              <button
                type="button"
                class="btn btn-sm btn-outline-danger w-100"
                (click)="removeFilter(i)"
              >
                <span class="material-symbols-outlined">delete</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    }
  </div>

  @if (form.get('metricType')!.value === AnalyticsMetricType.AGGREGATE) {
    <hr class="my-3" />

    <label>{{
      'dashboard.edit.JobResultAnalyticsDashboardComponent.groupingMode'
        | translate
    }}</label>
    <p-select
      formControlName="groupingMode"
      [options]="groupingModeOptions"
      optionLabel="key"
      optionValue="value"
      class="w-100"
    >
      <ng-template let-selectedOption pTemplate="selectedItem">
        {{
          'dashboard.edit.JobResultAnalyticsDashboardComponent.groupingModes.' +
            selectedOption.key | translate
        }}
      </ng-template>
      <ng-template let-option pTemplate="item">
        {{
          'dashboard.edit.JobResultAnalyticsDashboardComponent.groupingModes.' +
            option.key | translate
        }}
      </ng-template>
    </p-select>

    @if (form.get('groupingMode')!.value === GroupingMode.FIELD) {
      <label>{{
        'dashboard.edit.JobResultAnalyticsDashboardComponent.groupByField'
          | translate
      }}</label>
      <p-select
        formControlName="groupBy"
        [options]="getFieldOptions()"
        optionLabel="label"
        optionValue="value"
        [filter]="true"
        [showClear]="true"
        class="w-100"
      />
    }

    @if (form.get('groupingMode')!.value === GroupingMode.DATE) {
      <label>{{
        'dashboard.edit.JobResultAnalyticsDashboardComponent.dateGranularity'
          | translate
      }}</label>
      <p-select
        formControlName="dateGroupByGranularity"
        [options]="dateGranularityOptions"
        optionLabel="key"
        optionValue="value"
        class="w-100"
      >
        <ng-template let-selectedOption pTemplate="selectedItem">
          {{
            'dashboard.edit.JobResultAnalyticsDashboardComponent.dateGranularityOptions.' +
              selectedOption.key | translate
          }}
        </ng-template>
        <ng-template let-option pTemplate="item">
          {{
            'dashboard.edit.JobResultAnalyticsDashboardComponent.dateGranularityOptions.' +
              option.key | translate
          }}
        </ng-template>
      </p-select>
    }

    <label>
      {{
        (form.get('groupingMode')!.value === GroupingMode.DATE
          ? 'dashboard.edit.JobResultAnalyticsDashboardComponent.limitDateGrouping'
          : 'dashboard.edit.JobResultAnalyticsDashboardComponent.limit'
        ) | translate
      }}
    </label>
    <input
      type="number"
      formControlName="limit"
      class="form-control"
      min="1"
      [max]="maxLimit"
      [placeholder]="'Max ' + maxLimit"
    />
  }
</form>
